'use client';

import { motion } from 'motion/react';
import Link from 'next/link';

import { REVEAL_STAGGER, TextReveal } from '@/components/elements/text-reveal';
import { appStoreUrl } from '@/lib/constants';

interface ClosingCtaProps {
  title: string;
  subtitle?: string;
  /** Kept for call-site compatibility; the redesigned panel has no image. */
  image?: string;
  /** App Store campaign token for the CTA. */
  ctaCampaign?: string;
  /** CTA button label. */
  ctaLabel?: string;
}

export function ClosingCta({
  title,
  subtitle,
  ctaCampaign = 'web_usecase_cta',
  ctaLabel = 'Create your album',
}: ClosingCtaProps) {
  return (
    <section className="section-padding">
      <div className="container">
        <motion.div
          className="dark:border-border dark:bg-surface rounded-xl bg-[#121212] px-8 py-16 text-center md:py-[72px] dark:border"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <TextReveal mode="animate">
            <h2 className="font-display text-3xl tracking-tight text-balance text-white md:text-[44px] md:leading-tight">
              {title}
            </h2>
          </TextReveal>
          {subtitle && (
            <TextReveal mode="animate" delay={REVEAL_STAGGER}>
              <p className="font-display mt-1 text-3xl tracking-tight text-white/50 md:text-[44px] md:leading-tight">
                {subtitle}
              </p>
            </TextReveal>
          )}
          <motion.div
            className="mt-9"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 0.4 }}
          >
            <Link
              href={appStoreUrl(ctaCampaign)}
              target="_blank"
              rel="noopener"
              className="inline-flex h-[52px] items-center rounded-xl bg-white px-11 text-[15px] font-medium text-[#121212] no-underline transition-opacity hover:opacity-90"
            >
              {ctaLabel}
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
